import { createElement, Fragment } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import useMartyContext from 'hooks/useMartyContext';
import LandingPageLink from 'components/landing/LandingPageLink';
import LandingPageMedia from 'components/landing/LandingPageMedia';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';

import css from 'styles/components/common/contentBox.scss';

const getLinkClass = linkStyle => {
  switch (linkStyle) {
    case 'link-uppercase-bold':
      return css.linkUppercaseBold;
    default:
      return css.linkBtn;
  }
};

export const ContentBox = ({
  heading, copy, className, children, imageData, links, onClick, eventLabel, linkStyle,
  useImageAsHeading, shouldLazyLoad, backgroundColor, color, textAlign, contentWidth
}) => {
  const { testId } = useMartyContext();

  if (!heading && !copy && !imageData && !links?.length) {
    return null;
  }

  const style = {};
  if (backgroundColor) {
    style.backgroundColor = backgroundColor;
  }
  if (textAlign) {
    style.textAlign = textAlign;
  }
  if (contentWidth) {
    style.width = contentWidth;
  }
  if (color) {
    style.color = color;
    style.borderColor = color;
  }

  const hideHeadingForImg = useImageAsHeading && imageData?.alt;

  return (
    <div style={style} className={cn(css.container, className)} data-test-id={testId(`contentBox:${eventLabel || heading}`)}>
      {(heading && !hideHeadingForImg) && <h2 className={css.heading}>{heading}</h2>}
      {/* Wrap image in an h2 if we want that as our heading */}
      {createElement(
        hideHeadingForImg ? 'h2' : Fragment,
        null,
        <LandingPageMedia {...imageData} shouldLazyLoad={shouldLazyLoad}/>
      )}
      {copy && <p className={css.copy}>{copy}</p>}
      {children}
      {links?.map(({ href, text, newWindow, gae }) =>
        <LandingPageLink
          className={getLinkClass(linkStyle)}
          onClick={onClick}
          newWindow={newWindow}
          url={href}
          key={href + text + gae}
          data-eventvalue={gae || text}
          data-eventlabel={eventLabel}>
          {text}
        </LandingPageLink>
      )}
    </div>
  );
};

ContentBox.propTypes = {
  className: PropTypes.string,
  heading: PropTypes.string,
  copy: PropTypes.string,
  links: PropTypes.arrayOf(PropTypes.shape({
    href: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    newWindow: PropTypes.bool,
    gae: PropTypes.string
  })),
  linkStyle: PropTypes.string,
  eventLabel: PropTypes.string,
  children: PropTypes.node,
  onClick: PropTypes.func,
  shouldLazyLoad: PropTypes.bool,
  useImageAsHeading: PropTypes.bool,
  backgroundColor: PropTypes.string,
  color: PropTypes.string,
  textAlign: PropTypes.string,
  contentWidth: PropTypes.string,
  imageData: PropTypes.shape({
    type: PropTypes.string,
    sources: PropTypes.arrayOf(PropTypes.object),
    src: PropTypes.string,
    srcset: PropTypes.string,
    alt: PropTypes.string
  })
};

export default withErrorBoundary('ContentBox', ContentBox);
